import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from 'recharts';
import { buildIncomeSeries, buildIncomeSeriesFromReal } from '../../utils/chartApprox';
import { resolveChartColors } from '../theme';
import { LegendSwatch, ttStyle } from '../primitives';
import type { ReportEnrichment, WebReportPayload } from '../../types/webReport';

interface Props {
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  /** When set: fixed-size chart (print) with animations disabled. */
  width?: number;
}

/** Starting-plan income stack: withdrawals / SS / pension / wages per plan year. */
export function IncomeStackedChart({ payload, enrichment, width }: Props) {
  const colors = useMemo(() => resolveChartColors(), []);
  // Real SC01 income stack from SIPS (schema v8) when present; synthesized
  // sketch (input amounts + assumed start ages) for older reports.
  const series = useMemo(
    () =>
      payload.startingIncomeSeries && payload.startingIncomeSeries.length > 1
        ? buildIncomeSeriesFromReal(payload.startingIncomeSeries)
        : buildIncomeSeries({
            targetSpend: enrichment?.household.targetIncome ?? payload.client.targetSpend,
            planHorizonYears: payload.client.planHorizonYears,
            people: enrichment?.people ?? [],
          }),
    [payload, enrichment]
  );

  const animated = width == null;

  const chart = (
    <BarChart
      data={series}
      margin={{ top: 6, right: 6, left: 6, bottom: 18 }}
      {...(width != null ? { width, height: 260 } : {})}
    >
      <CartesianGrid vertical={false} stroke={colors.grayLine} />
      <XAxis dataKey="year" tick={{ fontSize: 11, fill: colors.gray }} tickLine={false} axisLine={{ stroke: colors.grayLine }}>
        <Label value="Years into plan" position="bottom" offset={4} style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600 }} />
      </XAxis>
      <YAxis
        tick={{ fontSize: 11, fill: colors.gray }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => `$${v}K`}
      >
        <Label
          value="Annual income"
          angle={-90}
          position="insideLeft"
          offset={16}
          style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600, textAnchor: 'middle' }}
        />
      </YAxis>
      <Tooltip contentStyle={ttStyle} formatter={(v) => `$${Math.round(Number(v))}K`} />
      <Bar dataKey="withdrawals" name="Withdrawals" stackId="a" fill={colors.navy} isAnimationActive={animated} />
      <Bar dataKey="socialSecurity" name="Social Security" stackId="a" fill={colors.teal} isAnimationActive={animated} />
      <Bar dataKey="pension" name="Pension" stackId="a" fill={colors.pension} isAnimationActive={animated} />
      <Bar
        dataKey="wages"
        name="Wages"
        stackId="a"
        fill={colors.wages}
        radius={[3, 3, 0, 0]}
        isAnimationActive={animated}
      />
    </BarChart>
  );

  return (
    <div>
      {width != null ? chart : <ResponsiveContainer width="100%" height={260}>{chart}</ResponsiveContainer>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: 12, color: colors.gray }}>
        <LegendSwatch color={colors.navy} label="Savings Withdrawals" />
        <LegendSwatch color={colors.teal} label="Social Security*" />
        <LegendSwatch color={colors.pension} label="Pension*" />
        <LegendSwatch color={colors.wages} label="Wages" />
      </div>
    </div>
  );
}
